import { BridgeComponent } from "@hotwired/hotwire-native-bridge"
import { canvasResize, PHOTO_THUMBNAIL } from "../lib/image_resize"
import { csrfToken, postJson, post } from "../lib/http"
import { uploadThumbnailsInChunks } from "../lib/thumbnail_backfill"

// Bridge controller that uploads photo library metadata to /library_photos/batch_create.
//
// Uses the "selectPhotos" bridge message which presents a native iOS action sheet:
//   • "Select All Photos"  — scans the full library
//   • "Choose Photos"      — opens the system photo picker for manual selection
//   • "Cancel"             — dismisses with no upload
//
// Nothing happens automatically on connect. The user must explicitly tap the button.
export default class extends BridgeComponent {
  static component = "photo-library"

  static targets = ["button", "scanning", "progress", "progressBar", "progressText", "done", "errorMessage", "errorText", "cancelButton"]
  // 0 = public/central library; >0 = upload these photos for that community only.
  // returnUrl = where to go after upload (the community page for a community upload,
  // else the Home feed).
  static values = { communityId: Number, returnUrl: String }

  connect() {
    super.connect()
    this._runId = 0
    this._phase = "idle"
    this._cancelRequested = false
    this._uploadedCount = 0
    this._newlyAddedCount = 0
    this._uploadTotal = 0
    this._uploadRunKey = null
    // this.bridge is only set when the native bridge is available.
    // On web, super.connect() returns early but JS continues executing here,
    // so we must guard explicitly before revealing the element.
    if (!this.bridge) return
    this.element.classList.remove("hidden")
  }

  disconnect() {
    this._runId += 1
    if (this._phase === "scanning" && this.bridge) {
      this.send("cancelScan", {}, () => {})
    }
    this._removeFallbackStatus()
    this._clearCompletionTimer()
    super.disconnect()
  }

  // Called when the user taps the upload button.
  // Sends "selectPhotos" to native → shows iOS action sheet.
  upload() {
    if (["scanning", "uploading", "finalizing"].includes(this._phase)) return

    this._clearCompletionTimer()
    const runId = ++this._runId
    this._phase = "scanning"
    this._cancelRequested = false
    this._uploadedCount = 0
    this._newlyAddedCount = 0
    this._uploadTotal = 0
    this._uploadRunKey = this._newUploadRunKey()
    this._setState("scanning")

    this.send("selectPhotos", {}, (response) => {
      if (runId !== this._runId) return

      const photos = response?.data?.photos || response?.photos
      const error  = response?.data?.error  || response?.error

      if (error === "scan_cancelled") {
        this._finishCancelledScan()
        return
      }

      // "choose_photos" means the user tapped "Choose Photos" in the action sheet.
      // The Swift side signals this via error so we can present the picker after
      // the action sheet finishes dismissing.
      if (error === "choose_photos") {
        this._pickPhotos(runId)
        return
      }

      // Empty array with no error = user cancelled the action sheet.
      if (!error && Array.isArray(photos) && photos.length === 0) {
        this._finishCancelledScan()
        return
      }

      if (error) {
        this._handleError(error)
        return
      }

      const list = Array.isArray(photos) ? photos : []

      if (list.length === 0) {
        this._showError("No photos with location data were found in your library.")
        return
      }

      if (!this._confirmImport(list.length)) {
        this._finishCancelledScan()
        return
      }

      this._uploadBatches(list, runId).catch((error) => this._handleUploadFailure(error))
    })
  }

  cancel(event) {
    event?.preventDefault()
    if (this._phase !== "scanning" && this._phase !== "uploading") return

    if (!window.confirm("Stop importing photos?")) return

    this._cancelRequested = true

    if (this._phase === "scanning") {
      this._runId += 1
      this.send("cancelScan", {}, () => {})
      this._finishCancelledScan()
    } else {
      this._showStoppingState()
    }
  }

  // ── Private ─────────────────────────────────────────────────────────────

  // Sends "pickPhotos" to open the system photo picker (no action sheet).
  _pickPhotos(runId) {
    this.send("pickPhotos", {}, (response) => {
      if (runId !== this._runId) return

      const photos = response?.data?.photos || response?.photos
      const error  = response?.data?.error  || response?.error

      if (error === "scan_cancelled") {
        this._finishCancelledScan()
        return
      }

      // Empty = user cancelled the picker
      if (!error && Array.isArray(photos) && photos.length === 0) {
        this._finishCancelledScan()
        return
      }

      if (error) {
        this._handleError(error)
        return
      }

      const list = Array.isArray(photos) ? photos : []

      if (list.length === 0) {
        this._showError("No photos with location data were found in your selection.")
        return
      }

      if (!this._confirmImport(list.length)) {
        this._finishCancelledScan()
        return
      }

      this._uploadBatches(list, runId).catch((error) => this._handleUploadFailure(error))
    })
  }

  async _uploadBatches(photos, runId) {
    if (runId !== this._runId) return

    this._phase = "uploading"
    this._setState("progress")

    // Large batches keep round-trips low — the server now bulk-upserts each batch
    // in a single query, so a 13k-photo library is ~26 requests instead of ~260.
    const BATCH_SIZE = 500
    const total = photos.length
    this._uploadTotal = total

    for (let i = 0; i < total; i += BATCH_SIZE) {
      if (this._cancelRequested || runId !== this._runId) break

      const batch = photos.slice(i, i + BATCH_SIZE)
      await this._sendBatch(batch)
      if (runId !== this._runId) return

      this._uploadedCount = Math.min(i + BATCH_SIZE, total)
      this._updateProgress(this._uploadedCount, total)
    }

    if (runId !== this._runId) return
    if (this._uploadedCount === 0) {
      this._finishCancelledScan()
      return
    }

    // All requested batches, or all batches completed before Stop was tapped, are
    // in. Finalizing the partial set makes those photos immediately matchable.
    this._phase = "finalizing"
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `Finding matches in ${this._uploadedCount.toLocaleString()} photos`
    }
    this._syncCancellationUI()
    const result = await this._finalize()
    const alreadyUpToDate = result.status === "already_up_to_date"

    // For a community upload, say so + go back to that community (where its matches
    // show); otherwise it's the public library → the Home feed.
    const community = this.communityIdValue > 0

    // Community uploads navigate away, so finish their thumbnail catch-up first.
    // Home stays mounted and photo-fetch handles this after MatchingChannel fires;
    // waiting here could leave the progress card up forever if iOS drops a callback.
    if (community && !alreadyUpToDate) await this._backfillMatchThumbnails()

    if (this.hasDoneTarget) {
      if (alreadyUpToDate) {
        this.doneTarget.textContent = "These photos have already been added. Your library is up to date."
      } else if (this._cancelRequested) {
        this.doneTarget.textContent = `${this._uploadedCount.toLocaleString()} photos added. Finding matches.`
      } else {
        this.doneTarget.textContent = "Photos uploaded. Finding matches."
      }
    }
    this._phase = "done"
    this._setState("done")

    // Some upload entry points do not render a completion target. Keep the
    // no-op result visible briefly there so the user understands what happened.
    if (alreadyUpToDate && !this.hasDoneTarget) {
      this._showFallbackStatus(
        "There’s nothing new to match.",
        "",
        true,
        false,
        "Photos already added"
      )
    }

    const destination = this.returnUrlValue && this.returnUrlValue.length > 0 ? this.returnUrlValue : "/discoveries"
    const navigate = () => {
      if (window.Turbo) {
        window.Turbo.visit(destination, { action: "replace" })
      } else {
        window.location.href = destination
      }
    }

    const navigateAfterResult = () => {
      if (alreadyUpToDate) {
        this._completionTimer = setTimeout(navigate, 1800)
      } else {
        navigate()
      }
    }

    if (community) {
      navigateAfterResult()
    } else {
      // Refresh Home as soon as the first public photo is stored. Matching remains
      // asynchronous; the refreshed page shows the invite state immediately and
      // its existing live feed updater replaces that state if matches arrive.
      navigateAfterResult()
    }
  }

  // After matching runs, fetch thumbnails for matched photos that have none
  // (typically iCloud-only photos). Uses the fetchPhoto bridge to download
  // the full image from iCloud, then generates a small thumbnail client-side.
  async _backfillMatchThumbnails() {
    try {
      const res = await fetch("/library_photos/match_missing_thumbnails", {
        headers: { "X-CSRF-Token": csrfToken() }
      })
      const { client_refs } = await res.json()
      await uploadThumbnailsInChunks(client_refs, (ref) => this._fetchPhotoThumbnail(ref))
    } catch (e) {
      // Non-fatal — thumbnails are cosmetic, don't block navigation
      console.warn("[library-upload] backfill thumbnails failed:", e)
    }
  }

  // Fetches a photo via the native bridge and returns a JPEG data URI, or null.
  _fetchPhotoThumbnail(localIdentifier) {
    return new Promise((resolve) => {
      this.send("fetchPhoto", { localIdentifier }, (response) => {
        const data = response?.data || response
        const b64  = data?.base64
        const mime = data?.mimeType || "image/jpeg"
        if (!b64) return resolve(null)
        resolve(canvasResize(`data:${mime};base64,${b64}`, PHOTO_THUMBNAIL))
      })
    })
  }

  async _sendBatch(batch) {
    const response = await postJson("/library_photos/batch_create", { metadata: batch, community_id: this.communityIdValue })
    if (!response.ok) throw new Error(`Metadata upload failed (${response.status})`)

    const result = await response.json()
    this._newlyAddedCount += Number(result.created) || 0
  }

  _confirmImport(count) {
    const noun = count === 1 ? "photo" : "photos"
    return window.confirm(
      `${count.toLocaleString()} ${noun} with location information ready to add. Continue?`
    )
  }

  // Run matching once after all batches are uploaded.
  async _finalize() {
    const response = await postJson("/library_photos/finalize", {
      community_id: this.communityIdValue,
      upload_run_key: this._uploadRunKey,
      upload_photo_count: this._uploadedCount,
      newly_added_photo_count: this._newlyAddedCount
    })
    if (!response.ok) throw new Error(`Photo matching failed (${response.status})`)

    const contentType = response.headers.get("content-type") || ""
    return contentType.includes("application/json") ? response.json() : {}
  }

  _newUploadRunKey() {
    return globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }

  _updateProgress(current, total) {
    const pct = Math.round((current / total) * 100)
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${pct}%`
    }
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent =
        `${current.toLocaleString()} of ${total.toLocaleString()} photos added`
    }
    this._updateFallbackStatus(`${current.toLocaleString()} of ${total.toLocaleString()} photos added`)
  }

  _setState(state) {
    const states = ["button", "scanning", "progress", "done", "errorMessage"]
    states.forEach(s => {
      const targetName = `${s}Target`
      const hasTarget  = `has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}`
      if (this[hasTarget]) {
        this[targetName].classList.toggle("hidden", s !== state)
      }
    })

    // Lock the modal against swipe-dismiss while a scan/upload is running so it
    // can't be interrupted by an accidental swipe-down; unlock when idle/done.
    if (this.bridge) {
      const busy = state === "scanning" || state === "progress"
      this.send(busy ? "lockScreen" : "unlockScreen", {}, () => {})
    }

    this._syncCancellationUI(state)
  }

  _syncCancellationUI(state = null) {
    const busy = this._phase === "scanning" || this._phase === "uploading"
    const finalizing = this._phase === "finalizing"
    const stopping = this._cancelRequested && this._phase === "uploading"
    const label = stopping ? "Stopping after this batch…" : "Stop importing"

    if (this.hasCancelButtonTarget) {
      this.cancelButtonTarget.classList.toggle("hidden", !busy)
      this.cancelButtonTarget.disabled = stopping
      this.cancelButtonTarget.textContent = label
    }

    if (this.hasCancelButtonTarget || (!busy && !finalizing)) {
      if (!busy && !finalizing) this._removeFallbackStatus()
      return
    }

    const detail = finalizing
      ? `Finding matches in ${this._uploadedCount.toLocaleString()} photos`
      : this._phase === "scanning"
        ? "Reading photo dates and locations"
        : `${this._uploadedCount.toLocaleString()} of ${this._uploadTotal.toLocaleString()} photos added`
    this._showFallbackStatus(detail, label, stopping, !finalizing)
  }

  _showFallbackStatus(detail, buttonLabel, disabled, showButton, heading = "Adding your photos") {
    if (!this._fallbackStatus) {
      const panel = document.createElement("div")
      panel.className = "library-upload-stop-panel"
      panel.setAttribute("role", "status")
      panel.innerHTML = `
        <div class="library-upload-stop-panel__copy">
          <strong>Adding your photos</strong>
          <span></span>
        </div>
        <button type="button"></button>
      `
      panel.querySelector("button").addEventListener("click", (event) => this.cancel(event))
      document.body.appendChild(panel)
      this._fallbackStatus = panel
    }

    this._fallbackStatus.querySelector("strong").textContent = heading
    this._fallbackStatus.querySelector("span").textContent = detail
    const button = this._fallbackStatus.querySelector("button")
    button.textContent = buttonLabel
    button.disabled = disabled
    button.classList.toggle("hidden", !showButton)
  }

  _updateFallbackStatus(detail) {
    if (this._fallbackStatus) this._fallbackStatus.querySelector("span").textContent = detail
  }

  _removeFallbackStatus() {
    this._fallbackStatus?.remove()
    this._fallbackStatus = null
  }

  _showStoppingState() {
    this._syncCancellationUI()
    this._updateFallbackStatus("Finishing the current batch, then finding matches")
  }

  _finishCancelledScan() {
    this._phase = "idle"
    this._cancelRequested = false
    this._setState("button")
  }

  _clearCompletionTimer() {
    if (!this._completionTimer) return
    clearTimeout(this._completionTimer)
    this._completionTimer = null
  }

  _handleUploadFailure(error) {
    console.warn("[library-upload] upload failed", error)
    this._phase = "idle"
    this._showError("Couldn't finish adding those photos. Please try again.")
  }

  _handleError(error) {
    if (error === "no_location_photos") {
      this._showError("That selection doesn't include any photos with location data. Try a photo taken with your phone's camera and Location Services enabled.")
    } else if (error === "permission_limited") {
      this._showError("Please allow access to all your photos (not just selected ones) in Settings, then try again.")
    } else if (error === "permission_denied") {
      this._showError("Photo library access is required. Please enable it in Settings → Privacy → Photos.")
    } else {
      this._showError("Something went wrong. Please try again.")
    }
  }

  _showError(message) {
    this._phase = "idle"
    if (this.hasErrorTextTarget) this.errorTextTarget.textContent = message
    this._setState("errorMessage")
  }
}
